import React, { useState } from 'react';
import { DENTAL_COLORS } from './dentalColorConfig';

// Compact tooth using SVG: lines from outer corners to inner square corners
const Tooth = ({ label, selectedSurfaces = {}, blockedSurfaces = {}, onToggle, showCompleteToothX = true, isTreatmentPlan = false, diagnosisMap = {} }) => {
  const displayLabel = String(label).replace('.', '');
  const [tooltip, setTooltip] = useState({ visible: false, content: null, x: 0, y: 0 });
  
  
  const surfaceLabel = (key) => {
    const toothNumber = label.replace('.', '');
    const quadrant = toothNumber.charAt(0);
    
    switch (key) {
      case 'top': 
        // Cuadrantes superiores (1,2,5,6): top = Vestibular
        // Cuadrantes inferiores (3,4,7,8): top = Lingual
        if (quadrant === '1' || quadrant === '2' || quadrant === '5' || quadrant === '6') {
          return 'Vestibular';
        } else {
          return 'Lingual';
        }
        
      case 'right': 
        // Cuadrantes derechos (1,4): right = Mesial
        // Cuadrantes izquierdos (2,3): right = Distal
        if (quadrant === '1' || quadrant === '4') {
          return 'Mesial';
        } else {
          return 'Distal';
        }
        
      case 'bottom': 
        // Cuadrantes superiores (1,2,5,6): bottom = Palatina
        // Cuadrantes inferiores (3,4,7,8): bottom = Vestibular
        if (quadrant === '1' || quadrant === '2' || quadrant === '5' || quadrant === '6') {
          return 'Palatina';
        } else {
          return 'Vestibular';
        }
        
      case 'left': 
        // Cuadrantes derechos (1,4): left = Distal
        // Cuadrantes izquierdos (2,3): left = Mesial
        if (quadrant === '1' || quadrant === '4') {
          return 'Distal';
        } else {
          return 'Mesial';
        }
        
      case 'center': 
        // El centro siempre es oclusal
        return 'Oclusal';
        
      default: 
        return key;
    }
  };

  // Función para generar contenido del tooltip personalizado
  const getTooltipContent = (surfaceKey, colorValue = null, isBlocked = false, blockedInfo = null) => {
    const surface = surfaceLabel(surfaceKey);
    
    // Información del diagnóstico si existe
    let diagnosisInfo = null;
    if (colorValue) {
      // Primero verificar si hay un diagnóstico mapeado para este diente+superficie específico
      const mapKey = `${label}:${surfaceKey}`;
      const mappedDiagnosis = diagnosisMap[mapKey];
      
      if (mappedDiagnosis) {
        // Usar el diagnóstico del mapeo
        diagnosisInfo = {
          name: mappedDiagnosis.name,
          color: mappedDiagnosis.color || colorValue,
          bgColor: mappedDiagnosis.color || colorValue
        };
      } else {
        // Si no hay mapeo específico, intentar encontrar en DENTAL_COLORS
        const isHexColor = colorValue.startsWith('#');
        const colorInfo = !isHexColor ? DENTAL_COLORS.find(c => c.id === colorValue) : null;
        
        if (colorInfo) {
          diagnosisInfo = {
            name: colorInfo.name,
            color: colorInfo.color,
            bgColor: colorInfo.bgColor
          };
        } else if (isHexColor) {
          // Si es un color hex sin mapeo, no mostrar "Color personalizado", solo la superficie
          diagnosisInfo = null;
        }
      }
    }
    
    // Si está bloqueado, mostrar información del procedimiento (solo para plan de tratamiento)
    if (isBlocked && blockedInfo && blockedInfo.procedureName) {
      diagnosisInfo = {
        name: blockedInfo.procedureName,
        color: '#38b6ff',
        bgColor: 'rgba(56, 182, 255, 0.6)'
      };
    }
    
    return {
      toothNumber: displayLabel,
      surfaceName: surface,
      diagnosis: diagnosisInfo,
      isBlocked: isBlocked,
      blockedBy: blockedInfo ? blockedInfo.procedureName : null,
      isTreatmentPlan: isBlocked && blockedInfo && blockedInfo.procedureName // Flag para identificar si es plan de tratamiento
    };
  };

  // Funciones para manejar el tooltip personalizado
  const handleMouseEnter = (event, surfaceKey, colorValue = null, isBlocked = false, blockedInfo = null) => {
    const content = getTooltipContent(surfaceKey, colorValue, isBlocked, blockedInfo);
    
    // Usar la posición exacta del cursor
    setTooltip({
      visible: true,
      content: content,
      x: event.clientX,
      y: event.clientY - 10
    });
  };

  const handleMouseMove = (event, surfaceKey, colorValue = null, isBlocked = false, blockedInfo = null) => {
    if (tooltip.visible) {
      // Actualizar posición del tooltip mientras se mueve el mouse
      setTooltip(prev => ({
        ...prev,
        x: event.clientX,
        y: event.clientY - 10
      }));
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, content: null, x: 0, y: 0 });
  };


  // Verificar si el diente está completamente marcado (todas las superficies tienen el mismo color)
  const isCompleteTooth = () => {
    // En el plan de tratamiento no se marca como diente completo
    if (isTreatmentPlan) return false;
    
    const surfaces = ['top', 'right', 'bottom', 'left', 'center'];
    const surfaceColors = surfaces.map(surface => selectedSurfaces[surface]).filter(Boolean);
    
    if (surfaceColors.length !== 5) return false; // No todas las superficies están marcadas
    
    // Verificar que todas las superficies tengan el mismo color
    const firstColor = surfaceColors[0];
    return surfaceColors.every(color => color === firstColor);
  };

  const isToothComplete = isCompleteTooth() && showCompleteToothX;
  const completeToothColor = isToothComplete ? selectedSurfaces['top'] : null;
  const size = 50;
  const pad = 2;
  const center = size / 2; // 25
  const half = 9; // half of center square side (18x18)
  // Coordenadas para los polígonos (superficies) - completas, se recortarán con clip-path
  const tl_poly = { x: pad, y: pad };
  const tr_poly = { x: size - pad, y: pad };
  const br_poly = { x: size - pad, y: size - pad };
  const bl_poly = { x: pad, y: size - pad };
  
  // Coordenadas para las líneas divisorias - ajustadas para no salirse
  const borderOffset = 2;
  const tl_line = { x: pad + borderOffset, y: pad + borderOffset };
  const tr_line = { x: size - pad - borderOffset, y: pad + borderOffset };
  const br_line = { x: size - pad - borderOffset, y: size - pad - borderOffset };
  const bl_line = { x: pad + borderOffset, y: size - pad - borderOffset };
  const ctl = { x: center - half, y: center - half };
  const ctr = { x: center + half, y: center - half };
  const cbr = { x: center + half, y: center + half };
  const cbl = { x: center - half, y: center + half };

  const polyPoints = {
    top: `${tl_poly.x},${tl_poly.y} ${tr_poly.x},${tr_poly.y} ${ctr.x},${ctr.y} ${ctl.x},${ctl.y}`,
    right: `${tr_poly.x},${tr_poly.y} ${br_poly.x},${br_poly.y} ${cbr.x},${cbr.y} ${ctr.x},${ctr.y}`,
    bottom: `${br_poly.x},${br_poly.y} ${bl_poly.x},${bl_poly.y} ${cbl.x},${cbl.y} ${cbr.x},${cbr.y}`,
    left: `${bl_poly.x},${bl_poly.y} ${tl_poly.x},${tl_poly.y} ${ctl.x},${ctl.y} ${cbl.x},${cbl.y}`,
  };

  return (
    <div className="tooth">
      <svg
        className="tooth-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`Diente ${displayLabel}`}
      >
        <title>{`Diente ${displayLabel}`}</title>
        {/* Definir máscara para esquinas redondeadas */}
        <defs>
          <clipPath id={`rounded-mask-${displayLabel}`}>
            <rect x={pad} y={pad} width={size - pad * 2} height={size - pad * 2} rx="6" ry="6" />
          </clipPath>
        </defs>
        
        {/* Outer box - siempre fondo blanco */}
        <rect 
          x={pad} 
          y={pad} 
          width={size - pad * 2} 
          height={size - pad * 2} 
          rx="6" 
          ry="6" 
          fill="#fff" 
          stroke="#4b5563" 
          strokeWidth="1.5" 
        />

        {/* Quadrant clickable regions - solo mostrar si no es diente completo */}
        {!isToothComplete && (['top','right','bottom','left']).map(key => {
          const colorValue = selectedSurfaces[key];
          const isBlocked = blockedSurfaces[key] && blockedSurfaces[key].blocked;
          
          // Determinar si es un color hex directo o un ID de color
          const isHexColor = colorValue && colorValue.startsWith('#');
          const colorInfo = !isHexColor && colorValue ? DENTAL_COLORS.find(c => c.id === colorValue) : null;
          const finalColor = isHexColor ? colorValue : (colorInfo ? colorInfo.bgColor : 'transparent');
          
          return (
            <polygon
              key={key}
              className={`region ${key} ${colorValue ? 'active' : ''} ${isBlocked ? 'blocked' : ''}`}
              points={polyPoints[key]}
              fill={finalColor}
              stroke={isBlocked ? '#ff6b6b' : 'none'}
              strokeWidth={isBlocked ? '2' : '0'}
              strokeDasharray={isBlocked ? '3,3' : 'none'}
              clipPath={`url(#rounded-mask-${displayLabel})`}
              style={{
                fill: finalColor,
                stroke: isBlocked ? '#ff6b6b' : 'none',
                strokeWidth: isBlocked ? '2' : '0',
                strokeDasharray: isBlocked ? '3,3' : 'none',
                opacity: isBlocked ? 0.6 : 1
              }}
              onClick={() => !isBlocked && onToggle(label, key)}
              onMouseEnter={(e) => handleMouseEnter(e, key, colorValue, isBlocked, blockedSurfaces[key])}
              onMouseMove={(e) => handleMouseMove(e, key, colorValue, isBlocked, blockedSurfaces[key])}
              onMouseLeave={handleMouseLeave}
            >
            </polygon>
          );
        })}

        {/* Center square clickable - solo mostrar si no es diente completo */}
        {!isToothComplete && (() => {
          const colorValue = selectedSurfaces['center'];
          const isBlocked = blockedSurfaces['center'] && blockedSurfaces['center'].blocked;
          
          // Determinar si es un color hex directo o un ID de color
          const isHexColor = colorValue && colorValue.startsWith('#');
          const colorInfo = !isHexColor && colorValue ? DENTAL_COLORS.find(c => c.id === colorValue) : null;
          const finalColor = isHexColor ? colorValue : (colorInfo ? colorInfo.bgColor : 'transparent');
          
          return (
            <rect
              x={center - half}
              y={center - half}
              width={half * 2}
              height={half * 2}
              className={`region center ${colorValue ? 'active' : ''} ${isBlocked ? 'blocked' : ''}`}
              fill={finalColor}
              stroke={isBlocked ? '#ff6b6b' : '#4b5563'}
              strokeWidth={isBlocked ? '2' : '1.5'}
              strokeDasharray={isBlocked ? '3,3' : 'none'}
              clipPath={`url(#rounded-mask-${displayLabel})`}
              style={{
                fill: finalColor,
                stroke: isBlocked ? '#ff6b6b' : '#4b5563',
                strokeWidth: isBlocked ? '2' : '1.5',
                strokeDasharray: isBlocked ? '3,3' : 'none',
                opacity: isBlocked ? 0.6 : 1
              }}
              onClick={() => !isBlocked && onToggle(label, 'center')}
              onMouseEnter={(e) => handleMouseEnter(e, 'center', colorValue, isBlocked, blockedSurfaces['center'])}
              onMouseMove={(e) => handleMouseMove(e, 'center', colorValue, isBlocked, blockedSurfaces['center'])}
              onMouseLeave={handleMouseLeave}
            >
            </rect>
          );
        })()}

        {/* Divider lines from corners to center square corners - solo mostrar si no es diente completo */}
        {!isToothComplete && (
          <>
            <line x1={tl_line.x} y1={tl_line.y} x2={ctl.x} y2={ctl.y} stroke="#4b5563" strokeWidth="1.5" />
            <line x1={tr_line.x} y1={tr_line.y} x2={ctr.x} y2={ctr.y} stroke="#4b5563" strokeWidth="1.5" />
            <line x1={br_line.x} y1={br_line.y} x2={cbr.x} y2={cbr.y} stroke="#4b5563" strokeWidth="1.5" />
            <line x1={bl_line.x} y1={bl_line.y} x2={cbl.x} y2={cbl.y} stroke="#4b5563" strokeWidth="1.5" />
          </>
        )}

        {/* X para diente completo */}
        {isToothComplete && (
          <>
            {/* Área clickeable invisible que cubre todo el diente */}
            <rect
              x={pad}
              y={pad}
              width={size - pad * 2}
              height={size - pad * 2}
              fill="transparent"
              onClick={() => onToggle(label, 'top')} // Usar 'top' como representativo del diente completo
              style={{ cursor: 'pointer' }}
              onMouseEnter={(e) => {
                // Obtener el diagnóstico del mapeo usando diente+superficie (usamos 'top' como representativo)
                let diagnosisInfo = null;
                if (completeToothColor) {
                  const mapKey = `${label}:top`;
                  const mappedDiagnosis = diagnosisMap[mapKey];
                  if (mappedDiagnosis) {
                    diagnosisInfo = {
                      name: mappedDiagnosis.name,
                      color: mappedDiagnosis.color || completeToothColor,
                      bgColor: mappedDiagnosis.color || completeToothColor
                    };
                  } else {
                    // Si no hay mapeo específico, intentar encontrar en DENTAL_COLORS
                    const isHexColor = completeToothColor.startsWith('#');
                    const colorInfo = !isHexColor ? DENTAL_COLORS.find(c => c.id === completeToothColor) : null;
                    if (colorInfo) {
                      diagnosisInfo = {
                        name: colorInfo.name,
                        color: colorInfo.color,
                        bgColor: colorInfo.bgColor
                      };
                    }
                  }
                }
                
                const content = {
                  toothNumber: displayLabel,
                  surfaceName: 'Diente completo',
                  diagnosis: diagnosisInfo
                };
                
                setTooltip({
                  visible: true,
                  content: content,
                  x: e.clientX,
                  y: e.clientY - 10
                });
              }}
              onMouseMove={(e) => {
                if (tooltip.visible) {
                  setTooltip(prev => ({
                    ...prev,
                    x: e.clientX,
                    y: e.clientY - 10
                  }));
                }
              }}
              onMouseLeave={handleMouseLeave}
            >
            </rect>
            
            {/* X visual con el color del diagnóstico - más grande y vibrante */}
            <g className="complete-tooth-x">
              {(() => {
                const isHexColor = completeToothColor && completeToothColor.startsWith('#');
                const colorInfo = !isHexColor && completeToothColor ? DENTAL_COLORS.find(c => c.id === completeToothColor) : null;
                // Usar el color principal del diagnóstico (más vibrante que bgColor)
                const xColor = isHexColor ? completeToothColor : (colorInfo ? colorInfo.color : '#4b5563');
                
                return (
                  <>
                    {/* Borde blanco para mejor contraste */}
                    <line 
                      x1={pad + 4} 
                      y1={pad + 4} 
                      x2={size - pad - 4} 
                      y2={size - pad - 4} 
                      stroke="#fff" 
                      strokeWidth="6" 
                      strokeLinecap="round"
                    />
                    <line 
                      x1={size - pad - 4} 
                      y1={pad + 4} 
                      x2={pad + 4} 
                      y2={size - pad - 4} 
                      stroke="#fff" 
                      strokeWidth="6" 
                      strokeLinecap="round"
                    />
                    {/* X principal más proporcionada */}
                    <line 
                      x1={pad + 4} 
                      y1={pad + 4} 
                      x2={size - pad - 4} 
                      y2={size - pad - 4} 
                      stroke={xColor} 
                      strokeWidth="4" 
                      strokeLinecap="round"
                    />
                    <line 
                      x1={size - pad - 4} 
                      y1={pad + 4} 
                      x2={pad + 4} 
                      y2={size - pad - 4} 
                      stroke={xColor} 
                      strokeWidth="4" 
                      strokeLinecap="round"
                    />
                  </>
                );
              })()}
            </g>
          </>
        )}
      </svg>
      <div className="tooth-label">{displayLabel}</div>
      
      {/* Custom Tooltip */}
      {tooltip.visible && tooltip.content && (
        <div 
          className="custom-dental-tooltip visible"
          style={{
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div className="tooltip-header">
            <span className="surface-name">
              {tooltip.content.diagnosis 
                ? `${tooltip.content.surfaceName} - ${tooltip.content.diagnosis.name}`
                : tooltip.content.surfaceName
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const adultTeeth = [
  // Arco superior: 18..11, 21..28
  ['1.8','1.7','1.6','1.5','1.4','1.3','1.2','1.1','2.1','2.2','2.3','2.4','2.5','2.6','2.7','2.8'],
  // Arco inferior: 48..41, 31..38
  ['4.8','4.7','4.6','4.5','4.4','4.3','4.2','4.1','3.1','3.2','3.3','3.4','3.5','3.6','3.7','3.8'],
];

const pediatricTeeth = [
  // Arco superior: 55..51, 61..65
  ['5.5','5.4','5.3','5.2','5.1','6.1','6.2','6.3','6.4','6.5'],
  // Arco inferior: 85..81, 71..75
  ['8.5','8.4','8.3','8.2','8.1','7.1','7.2','7.3','7.4','7.5'],
];

const Odontogram = ({ mode = 'adult', selected = {}, blocked = {}, onToggle, showCompleteToothX = true, isTreatmentPlan = false, diagnosisMap = {} }) => {
  const rows = mode === 'adult' ? adultTeeth : pediatricTeeth;
  const rowLabels = mode === 'adult'
    ? ['Arco superior (18–28)', 'Arco inferior (48–38)']
    : ['Arco superior (55–65)', 'Arco inferior (85–75)'];

  return (
    <div className={`odontogram ${mode}`}>
      {rows.map((row, idx) => (
        <React.Fragment key={idx}>
          <div className="row-label">{rowLabels[idx]}</div>
          <div className="tooth-row">
            {row.map(label => (
              <Tooth
                key={label}
                label={label}
                selectedSurfaces={selected[label] || {}}
                blockedSurfaces={blocked[label] || {}}
                onToggle={onToggle}
                showCompleteToothX={showCompleteToothX}
                isTreatmentPlan={isTreatmentPlan}
                diagnosisMap={diagnosisMap}
              />
            ))}
          </div>
          {idx === 0 && <div className="arc-separator" />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default Odontogram;


